// cryptoHelpers.js
// WebCrypto helpers for RSA-OAEP (SHA-256) + AES-CBC (256)

const textEncoder = new TextEncoder();
const textDecoder = new TextDecoder();

/* ====== UTIL: base64 <-> ArrayBuffer ====== */
export function arrayBufferToBase64(buffer: any) {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

export function base64ToArrayBuffer(base64: any) {
  const binary = atob(base64);
  const len = binary.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
}

export function safeBase64ToUint8(base64: string): Uint8Array {
  const clean = base64
    .replace(/-----.*?-----/g, "")   // remove PEM headers
    .replace(/\s+/g, "")             // remove spaces & newlines
    .replace(/-/g, "+")              // URL-safe fix
    .replace(/_/g, "/");             // URL-safe fix

  const padded = clean.padEnd(clean.length + (4 - clean.length % 4) % 4, "=");

  const binary = atob(padded);
  return Uint8Array.from(binary, (c) => c.charCodeAt(0));
}


/* ====== UTIL: PEM -> ArrayBuffer (strips header/footer) ====== */
export function pemToArrayBuffer(pem: string): any {
  const bytes = safeBase64ToUint8(pem);
  return bytes.buffer;
}




/* ====== RSA PUBLIC KEY import (SPKI) ====== */

export async function importRsaPublicKey(pem: string) {
  return crypto.subtle.importKey(
    "spki",
    pemToArrayBuffer(pem),
    { name: "RSA-OAEP", hash: "SHA-256" },
    false,
    ["encrypt"]
  );
}


/* ====== RSA PRIVATE KEY import (PKCS8) ====== */
export async function importRsaPrivateKey(pem: any) {
  const buf = pemToArrayBuffer(pem);
  return crypto.subtle.importKey(
    "pkcs8",
    buf,
    {
      name: "RSA-OAEP",
      hash: "SHA-256",
    },
    false,
    ["decrypt"]
  );
}

/* ====== RSA-OAEP encrypt (with public key) ====== */
export async function rsaEncryptRawKey(publicKeyPem: any, rawKeyArrayBuffer: any) {
  const key = await importRsaPublicKey(publicKeyPem);
  const encrypted = await crypto.subtle.encrypt({ name: "RSA-OAEP" }, key, rawKeyArrayBuffer);
  return arrayBufferToBase64(encrypted); // base64 for transport
}

/* ====== RSA-OAEP decrypt (returns ArrayBuffer raw AES key) ====== */
export async function rsaDecryptToRawKey(privateKeyPem: any, base64Encrypted: any) {
  const key = await importRsaPrivateKey(privateKeyPem);
  const encryptedBuf = base64ToArrayBuffer(base64Encrypted);
  const decrypted = await crypto.subtle.decrypt({ name: "RSA-OAEP" }, key, encryptedBuf);
  return decrypted; // ArrayBuffer (raw AES key bytes)
}

/* ====== AES helpers ====== */
/* Generate AES-256 raw key (ArrayBuffer of 32 bytes) and CryptoKey */
export async function generateAesKey() {
  // create CryptoKey, export raw
  const cryptoKey = await crypto.subtle.generateKey(
    { name: "AES-CBC", length: 256 },
    true,
    ["encrypt", "decrypt"]
  );

  const raw = await crypto.subtle.exportKey("raw", cryptoKey); // ArrayBuffer
  return { cryptoKey, raw }; // raw is ArrayBuffer (32 bytes)
}

/* Import raw AES key ArrayBuffer into CryptoKey */
export async function importAesKeyFromRaw(rawArrayBuffer: any) {
  return crypto.subtle.importKey(
    "raw",
    rawArrayBuffer,
    { name: "AES-CBC" },
    false,
    ["encrypt", "decrypt"]
  );
}

/* AES-CBC encrypt: returns base64 ciphertext and base64 iv */
export async function aesEncryptJSON(rawAesKeyArrayBuffer: any, plainObj: any) {
  const iv = crypto.getRandomValues(new Uint8Array(16)); // 16 bytes
  const aesKey = await importAesKeyFromRaw(rawAesKeyArrayBuffer);
  const plaintext = textEncoder.encode(JSON.stringify(plainObj));
  const ct = await crypto.subtle.encrypt({ name: "AES-CBC", iv }, aesKey, plaintext);
  return {
    data: arrayBufferToBase64(ct),
    iv: arrayBufferToBase64(iv.buffer),
  };
}
function deepParseJSON(input: string) {
  let data = input;
  while (typeof data === "string") {
    data = JSON.parse(data);
  }
  return data;
}
function safeJsonParse(raw: string) {
  if (!raw) return raw;

  let s = raw.trim();

  // 1. Remove outer quotes (single OR double)
  if (
    (s.startsWith('"') && s.endsWith('"')) ||
    (s.startsWith("'") && s.endsWith("'"))
  ) {
    s = s.slice(1, -1);
  }

  // 2. Try direct JSON parse
  try {
    return deepParseJSON(s);
  } catch {}

  // 3. If failed → probably escaped JSON → unescape \" → "
  const unescaped = s.replace(/\\"/g, '"');

  try {
    return JSON.parse(unescaped);
  } catch (err) {
    console.error("safeJsonParse failed for:", raw);
    throw err;
  }
}

/* AES-CBC decrypt: ciphertextBase64 + ivBase64 + rawAesKeyArrayBuffer -> parsed JSON */
export async function aesDecryptJSON(rawAesKeyArrayBuffer: any, ciphertextBase64: any, ivBase64: any) {
  const aesKey = await importAesKeyFromRaw(rawAesKeyArrayBuffer);
  const ctBuf = base64ToArrayBuffer(ciphertextBase64);
  const ivBuf = base64ToArrayBuffer(ivBase64);
  const plainBuf = await crypto.subtle.decrypt({ name: "AES-CBC", iv: new Uint8Array(ivBuf) }, aesKey, ctBuf);
  const jsonStr = textDecoder.decode(plainBuf);
  return safeJsonParse(jsonStr);
}

/* Utility: clean PEM from env (replace \n with actual newlines and trim) */
export function normalizePemFromEnv(pemEnvString: any) {
  if (!pemEnvString) throw new Error("PEM string not provided");
  // If the env value contains literal "\n", convert them. If it already contains real newlines, keep them.
  const replaced = pemEnvString.includes("\\n") ? pemEnvString.replace(/\\n/g, "\n") : pemEnvString;
  return replaced.trim();
}
